import { Component } from "@theme/component";
import { morphSection } from "@theme/section-renderer";
import { DiscountUpdateEvent } from "@theme/events";
import { fetchConfig, requestIdleCallback } from "@theme/utilities";
import { cartPerformance } from "@theme/performance";
import {
  existingApplicableCodesFromCart,
  getLoyaltyDiscountCfg,
  isLoyaltyCode,
  loyaltyMinErrorMessage,
} from "./loyalty-discount-utils.js";

/**
 * Cart line rows (not total quantity). Local to this module so deploys never depend on a separate utils export for this helper.
 *
 * @param {unknown} cartJson
 * @returns {number}
 */
function cartLineItemCount(cartJson) {
  if (!cartJson || typeof cartJson !== "object" || !("items" in cartJson)) return 0;
  const items = cartJson.items;
  return Array.isArray(items) ? items.length : 0;
}

/**
 * @param {string} raw
 * @returns {string[]}
 */
function parseUrlDiscountCodes(raw) {
  const parts = raw.split(",");
  /** @type {string[]} */
  const out = [];
  const seen = new Set();
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  const seen = new Set();
  for (const el of document.querySelectorAll("cart-items-component[data-section-id]")) {
    if (!(el instanceof HTMLElement)) continue;
    const sid = el.dataset.sectionId;
    if (!sid || seen.has(sid)) continue;
    seen.add(sid);
    ids.push(sid);
  }
  return ids;
}

function stripDiscountQueryParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("discount")) return;
  url.searchParams.delete("discount");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", next);
}

/**
 * @param {string} code
 * @param {string[]} existingDiscounts
 * @param {{ code: string; applicable: boolean }[]} discountCodesPayload
 * @param {string} newHtml
 * @param {string} sectionId
 * @returns {boolean}
 */
function isShippingOnlyDiscountCase(code, existingDiscounts, discountCodesPayload, newHtml, sectionId) {
  const parsedHtml = new DOMParser().parseFromString(newHtml, "text/html");
  const section = parsedHtml.getElementById(`shopify-section-${sectionId}`);
  const discountPills = section?.querySelectorAll(".cart-discount__pill") || [];
  if (!section) return false;
  const codes = Array.from(discountPills)
    .map(element => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
    .filter(Boolean);
  return (
    codes.length === existingDiscounts.length &&
    codes.every((/** @type {string} */ c) => existingDiscounts.includes(c)) &&
    Boolean(
      discountCodesPayload.find(discount => {
        return discount.code === code && discount.applicable === true;
      })
    )
  );
}

/** @type {boolean} */
let urlDiscountLock = false;

/**
 * Applies `discount` query param codes via Cart API (runs once; works when cart UI is empty).
 */
async function applyDiscountFromUrlIfPresent() {
  if (urlDiscountLock) return;
  const win = /** @type {Window & { Theme?: { routes?: { cart_update_url?: string; cart_url?: string } } }} */ (window);
  if (!win.Theme?.routes?.cart_update_url) return;

  const params = new URLSearchParams(window.location.search);
  const raw = params.get("discount");
  if (!raw?.trim()) return;

  const urlCodes = parseUrlDiscountCodes(raw);
  if (urlCodes.length === 0) return;

  urlDiscountLock = true;
  const abortController = new AbortController();
  const { signal } = abortController;

  try {
    const cartRes = await fetch(`${win.Theme.routes.cart_url}.js`, { signal });
    if (!cartRes.ok) return;

    const cartJson = await cartRes.json();
    const existing = existingApplicableCodesFromCart(cartJson);
    const subtotalCents = typeof cartJson.items_subtotal_price === "number" ? cartJson.items_subtotal_price : 0;

    const toAdd = urlCodes.filter(c => !existing.some(e => e.toLowerCase() === c.toLowerCase()));

    if (toAdd.length === 0) {
      stripDiscountQueryParam();
      return;
    }

    const loyaltyCfgUrl = getLoyaltyDiscountCfg();
    const { prefixUpper, minSubtotalCents } = loyaltyCfgUrl;
    const lineItemCnt = cartLineItemCount(cartJson);

    const loyaltyBlocked =
      toAdd.every(c => isLoyaltyCode(c, prefixUpper)) && (lineItemCnt === 1 || subtotalCents < minSubtotalCents);
    if (loyaltyBlocked) {
      return;
    }

    const eligible = toAdd.filter(c => {
      if (!isLoyaltyCode(c, prefixUpper)) return true;
      if (lineItemCnt === 1) return false;
      return subtotalCents >= minSubtotalCents;
    });
    if (eligible.length === 0) return;

    const skippedLoyalty = eligible.length < toAdd.length;

    const merged = [...existing];
    for (const c of eligible) {
      if (!merged.some(m => m.toLowerCase() === c.toLowerCase())) merged.push(c);
    }

    const sectionIds = getCartSectionIds();
    /** @type {Record<string, unknown>} */
    const body = { discount: merged.join(",") };
    if (sectionIds.length) body.sections = sectionIds;

    const config = fetchConfig("json", {
      body: JSON.stringify(body),
    });

    const response = await fetch(win.Theme.routes.cart_update_url, { ...config, signal });
    const data = await response.json();

    const discountPayload = Array.isArray(data.discount_codes) ? data.discount_codes : [];

    for (const code of eligible) {
      if (
        discountPayload.find((/** @type {{ code: string; applicable: boolean }} */ d) => {
          return d.code === code && d.applicable === false;
        })
      ) {
        if (!skippedLoyalty) stripDiscountQueryParam();
        return;
      }
    }

    const primarySectionId = sectionIds[0];
    const newHtml = primarySectionId ? data.sections?.[primarySectionId] : null;

    if (primarySectionId && typeof newHtml === "string") {
      for (const code of eligible) {
        if (isShippingOnlyDiscountCase(code, existing, discountPayload, newHtml, primarySectionId)) {
          if (!skippedLoyalty) stripDiscountQueryParam();
          await morphSection(primarySectionId, newHtml);
          document.dispatchEvent(new DiscountUpdateEvent(data, "url-discount", { domSynced: true }));
          return;
        }
      }
    }

    if (primarySectionId && typeof newHtml === "string") {
      await morphSection(primarySectionId, newHtml);
      document.dispatchEvent(new DiscountUpdateEvent(data, "url-discount", { domSynced: true }));
    } else {
      document.dispatchEvent(new DiscountUpdateEvent(data, "url-discount"));
    }
    if (!skippedLoyalty) stripDiscountQueryParam();
  } catch (error) {
    console.error("Cart discount from URL failed:", error);
  } finally {
    urlDiscountLock = false;
  }
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorLoyalty - Loyalty minimum subtotal error element.
 * @property {HTMLElement} cartDiscountErrorEmpty - Empty discount field error element.
 * @property {HTMLButtonElement} cartDiscountApplyBtn - Apply discount submit button.
 */

const DISCLOSURE_CLOSE = "disclosure:close";

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    "cartDiscountError",
    "cartDiscountErrorDiscountCode",
    "cartDiscountErrorShipping",
    "cartDiscountErrorLoyalty",
    "cartDiscountErrorEmpty",
    "cartDiscountApplyBtn",
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {HTMLElement | null} */
  #disclosureHost = null;

  /** @type {(() => void) | null} */
  #onDisclosureCloseBound = null;

  connectedCallback() {
    super.connectedCallback();
    this.#disclosureHost = this.closest("disclosure-custom");
    this.#onDisclosureCloseBound = () => this.#clearDiscountErrors();
    this.#disclosureHost?.addEventListener(DISCLOSURE_CLOSE, this.#onDisclosureCloseBound);
  }

  disconnectedCallback() {
    if (this.#disclosureHost && this.#onDisclosureCloseBound) {
      this.#disclosureHost.removeEventListener(DISCLOSURE_CLOSE, this.#onDisclosureCloseBound);
    }
    this.#disclosureHost = null;
    this.#onDisclosureCloseBound = null;
    super.disconnectedCallback();
  }

  /**
   * Called when cart line updates remove a loyalty code below minimum subtotal.
   * @param {string} message
   */
  showLoyaltyThresholdError(message) {
    const disc = this.closest("disclosure-custom");
    if (disc && typeof disc.open === "function") {
      disc.open();
    }
    this.#handleDiscountError("loyalty", message);
  }

  #clearDiscountErrors() {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorLoyalty,
      cartDiscountErrorEmpty,
    } = this.refs;
    cartDiscountError.classList.add("hidden");
    cartDiscountErrorDiscountCode.classList.add("hidden");
    cartDiscountErrorShipping.classList.add("hidden");
    cartDiscountErrorLoyalty.classList.add("hidden");
    cartDiscountErrorEmpty.classList.add("hidden");
    const form = this.querySelector("form.cart-discount__form");
    const input = form?.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement) {
      input.removeAttribute("aria-invalid");
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * @param {boolean} isLoading
   */
  #setApplyBtnLoading(isLoading) {
    const btn = this.refs.cartDiscountApplyBtn;
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  /**
   * @param {HTMLButtonElement | null} btn
   * @param {boolean} isLoading
   */
  #setRemoveBtnLoading(btn, isLoading) {
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async event => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== "string") return;

    const discountCodeTrimmed = discountCode.value.trim();
    const sectionId = this.dataset.sectionId;

    const abortController = this.#createAbortController();

    try {
      this.#clearDiscountErrors();

      if (!discountCodeTrimmed) {
        this.#handleDiscountError("empty");
        return;
      }

      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeTrimmed)) return;

      this.#setApplyBtnLoading(true);

      const loyaltyCfg = getLoyaltyDiscountCfg();
      if (isLoyaltyCode(discountCodeTrimmed, loyaltyCfg.prefixUpper)) {
        const cartRes = await fetch(`${Theme.routes.cart_url}.js`, { signal: abortController.signal });
        if (cartRes.ok) {
          const cartJson = await cartRes.json();
          const lineItemCnt = cartLineItemCount(cartJson);
          const cur = typeof cartJson.currency === "string" ? cartJson.currency : "USD";
          const loyaltyErrMsg = loyaltyMinErrorMessage(loyaltyCfg.minSubtotalWhole, cur, loyaltyCfg.errorTemplate);
          if (lineItemCnt === 1) {
            this.#handleDiscountError("loyalty", loyaltyErrMsg);
            return;
          }
          const subtotalCents = typeof cartJson.items_subtotal_price === "number" ? cartJson.items_subtotal_price : 0;
          if (subtotalCents < loyaltyCfg.minSubtotalCents) {
            this.#handleDiscountError("loyalty", loyaltyErrMsg);
            return;
          }
        }
      }

      const config = fetchConfig("json", {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeTrimmed].join(","),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const discountPayload = Array.isArray(data.discount_codes) ? data.discount_codes : [];

      if (
        discountPayload.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeTrimmed && discount.applicable === false;
        })
      ) {
        discountCode.value = "";
        const postCfg = getLoyaltyDiscountCfg();
        if (isLoyaltyCode(discountCodeTrimmed, postCfg.prefixUpper)) {
          const lineItemCnt = cartLineItemCount(data);
          if (lineItemCnt === 1) {
            const cur = typeof data.currency === "string" ? data.currency : "USD";
            this.#handleDiscountError(
              "loyalty",
              loyaltyMinErrorMessage(postCfg.minSubtotalWhole, cur, postCfg.errorTemplate)
            );
            return;
          }
        }
        this.#handleDiscountError("discount_code");
        return;
      }

      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, "text/html");
      const section = parsedHtml.getElementById(`shopify-section-${sectionId}`);
      const discountCodes = section?.querySelectorAll(".cart-discount__pill") || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map(element => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          discountPayload.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeTrimmed && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError("shipping");
          discountCode.value = "";
          return;
        }
      }

      await morphSection(sectionId, newHtml);
      const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
      const appliedInput = sectionEl?.querySelector('cart-discount-component input[name="discount"]');
      if (appliedInput instanceof HTMLInputElement) {
        appliedInput.value = "";
      }
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id, { domSynced: true }));
    } catch (error) {
      console.error("Cart discount apply failed:", error);
    } finally {
      this.#setApplyBtnLoading(false);
      this.#activeFetch = null;
      cartPerformance.measureFromEvent("discount-update:user-action", event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async event => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== "Enter") ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== "string"
    ) {
      return;
    }

    const pill = event.target.closest(".cart-discount__pill");
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    const removeBtn = event.target.closest("button.cart-discount__pill-remove");
    if (!(removeBtn instanceof HTMLButtonElement)) return;

    this.dispatchEvent(new CustomEvent("cart-discount:manual-remove", { bubbles: true }));

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      this.#setRemoveBtnLoading(removeBtn, true);

      const config = fetchConfig("json", {
        body: JSON.stringify({ discount: existingDiscounts.join(","), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const sid = this.dataset.sectionId;
      const rmHtml = data.sections?.[sid];
      if (typeof rmHtml === "string") {
        await morphSection(sid, rmHtml);
        document.dispatchEvent(new DiscountUpdateEvent(data, this.id, { domSynced: true }));
      } else {
        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      }
    } catch (error) {
      console.error("Cart discount remove failed:", error);
    } finally {
      this.#setRemoveBtnLoading(removeBtn, false);
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'loyalty' | 'empty'} type - The type of discount error.
   * @param {string} [loyaltyMessage] - Filled loyalty error copy (built in JS when type is `loyalty`).
   */
  #handleDiscountError(type, loyaltyMessage) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorLoyalty,
      cartDiscountErrorEmpty,
    } = this.refs;
    cartDiscountErrorDiscountCode.classList.add("hidden");
    cartDiscountErrorShipping.classList.add("hidden");
    cartDiscountErrorLoyalty.classList.add("hidden");
    cartDiscountErrorEmpty.classList.add("hidden");
    cartDiscountError.classList.remove("hidden");
    const form = this.querySelector("form.cart-discount__form");
    const input = form?.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement && type === "empty") {
      input.setAttribute("aria-invalid", "true");
    }
    const target =
      type === "discount_code"
        ? cartDiscountErrorDiscountCode
        : type === "shipping"
          ? cartDiscountErrorShipping
          : type === "empty"
            ? cartDiscountErrorEmpty
            : cartDiscountErrorLoyalty;
    if (type === "loyalty" && typeof loyaltyMessage === "string") {
      cartDiscountErrorLoyalty.textContent = loyaltyMessage;
    }
    target.classList.remove("hidden");
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const scopeRoot = this.closest("cart-items-component");
    const scope = scopeRoot instanceof HTMLElement ? scopeRoot : this;
    const discountPills = scope.querySelectorAll(".cart-discount__pill");
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === "string") {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get("cart-discount-component")) {
  customElements.define("cart-discount-component", CartDiscount);
}

requestIdleCallback(() => {
  applyDiscountFromUrlIfPresent();
});
